import React, { useState, useEffect } from 'react';
import { Users, TrendingUp } from 'lucide-react';
import { db, doc, onSnapshot, handleFirestoreError, OperationType } from '../src/firebase';

export const FakeUserCounter: React.FC = () => {
  const [userTotal, setUserTotal] = useState<number>(0);
  const [userToday, setUserToday] = useState<number>(0);
  const [realStats, setRealStats] = useState({ total: 0, today: 0 });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'stats', 'visitors'), (snapshot) => {
      if (snapshot.exists()) {
        setRealStats(snapshot.data() as { total: number; today: number });
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'stats/visitors');
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Initial values or load from localStorage
    const storedTotal = localStorage.getItem('fake_user_total');
    const storedToday = localStorage.getItem('fake_user_today');
    const lastUpdateDate = localStorage.getItem('fake_user_last_date');
    const storedLimit = localStorage.getItem('fake_user_daily_limit');

    const todayStr = new Date().toLocaleDateString();

    let initialTotal = storedTotal ? parseInt(storedTotal) : 845231;
    
    // Force update if current total is less than 845231 as requested
    if (initialTotal < 845231) {
      initialTotal = 845231;
      localStorage.setItem('fake_user_total', initialTotal.toString());
    }

    let initialToday = storedToday ? parseInt(storedToday) : 612;
    let dailyLimit = storedLimit ? parseInt(storedLimit) : Math.floor(Math.random() * (773 - 612 + 1)) + 612;

    // Reset Today if it's a new day
    if (lastUpdateDate !== todayStr) {
      initialToday = Math.floor(Math.random() * 50) + 10; // Start with a small random number
      // Generate new daily limit between 612 and 773
      dailyLimit = Math.floor(Math.random() * (773 - 612 + 1)) + 612;
      
      localStorage.setItem('fake_user_last_date', todayStr);
      localStorage.setItem('fake_user_daily_limit', dailyLimit.toString());
      localStorage.setItem('fake_user_today', initialToday.toString());
    }

    setUserTotal(initialTotal);
    setUserToday(initialToday);

    const getGrowthConfig = () => {
      const now = new Date();
      const hour = now.getHours();
      
      // Speed logic
      if (hour >= 23 || hour < 3) return { min: 0, max: 1, interval: 30000 }; 
      if (hour >= 3 && hour < 6) return { min: 0, max: 2, interval: 20000 };
      if (hour >= 6 && hour < 10) return { min: 1, max: 5, interval: 10000 };
      if (hour >= 10 && hour < 16) return { min: 3, max: 10, interval: 8000 };
      if (hour >= 16 && hour < 23) return { min: 8, max: 20, interval: 5000 };
      
      return { min: 1, max: 3, interval: 15000 };
    };

    const updateCounts = () => {
      const currentLimit = parseInt(localStorage.getItem('fake_user_daily_limit') || "700");
      const currentToday = parseInt(localStorage.getItem('fake_user_today') || "0");

      if (currentToday >= currentLimit) {
        timeoutId = setTimeout(updateCounts, 60000);
        return;
      }

      const config = getGrowthConfig();
      let increment = Math.floor(Math.random() * (config.max - config.min + 1)) + config.min;
      
      if (currentToday + increment > currentLimit) {
        increment = currentLimit - currentToday;
      }

      if (increment > 0) {
        setUserTotal(prev => {
          const next = prev + increment;
          localStorage.setItem('fake_user_total', next.toString());
          return next;
        });
        setUserToday(prev => {
          const next = prev + increment;
          localStorage.setItem('fake_user_today', next.toString());
          return next;
        });
      }

      const nextInterval = config.interval + (Math.random() * 5000 - 2500);
      timeoutId = setTimeout(updateCounts, nextInterval);
    };

    let timeoutId = setTimeout(updateCounts, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  const displayTotal = Math.max(userTotal, realStats.total);
  const displayToday = Math.max(userToday, realStats.today);

  return (
    <div className="flex items-center space-x-4 px-4 py-1 bg-black/20 backdrop-blur-sm rounded-full border border-white/10">
      <div className="flex items-center space-x-2">
        <Users size={14} className="text-orange-400" />
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-400 uppercase font-bold leading-none">Total Users</span>
          <span className="text-xs font-black text-white tabular-nums">{displayTotal.toLocaleString()}</span>
        </div>
      </div>
      
      <div className="w-[1px] h-6 bg-white/10"></div>
      
      <div className="flex items-center space-x-2">
        <TrendingUp size={14} className="text-green-400" />
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-400 uppercase font-bold leading-none">Users Today</span>
          <span className="text-xs font-black text-white tabular-nums">{displayToday.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};
